import { apiGet } from './api';

/**
 * Fetch a page of carrier-engagement referrals from /api/v1/referrals/.
 *
 * The endpoint is portal-agnostic — security and scoping are derived from
 * request.user. CE sends its own filter set so the backend doesn't need to
 * know which UI is calling.
 *
 * @param {Object} opts
 * @param {string} [opts.cursor]   - Opaque cursor from a prior page's next_page.
 *                                   Omit for page 1.
 * @param {number} [opts.limit]    - Page size (server caps at 1000).
 * @param {string} [opts.search]   - Free-text search (id + referral_name).
 * @param {string} [opts.ordering] - Comma-separated sort fields, '-' prefix
 *                                   for descending (e.g. '-publish_dt,client').
 *                                   The backend sorts the full dataset before
 *                                   paginating, so the order reflects every
 *                                   matching referral, not just this page.
 * @param {Object} [opts.filters]  - Additional filter params keyed by the
 *                                   backend's filter names (client_id,
 *                                   tpa_id, aging_out, referral_type, …).
 *                                   tpa_id is the backend's name for what
 *                                   product/UI calls "partner."
 * @returns {Promise<{total_records:number, returned_records:number,
 *                    next_page:string|null, results:Array}>}
 */
export const fetchEngagementReferrals = async ({
  cursor,
  limit,
  search,
  ordering,
  filters = {},
} = {}) => {
  const params = new URLSearchParams();

  // OM/non-demo scoping is enforced at migration time
  // (migrate_relationships_from_legacy, migrate_user_access) — no need to send
  // those as query params.
  //
  // CE shows OPEN (active) referrals by default — the same set the Carrier
  // Portal shows, so the two surfaces agree on the open count. Closed/inactive
  // referrals are reached only through the dedicated "Closed" engagement-step
  // bucket, which the backend resolves as is_active=False; that bucket is empty
  // unless the inactive set is in scope, so include_closed is sent only then.
  // (Sending it unconditionally was the bug: the default view counted closed
  // referrals too, overstating the open count vs the Carrier Portal.)
  //
  // The last-comment column is external-only on every surface — internal (SL)
  // comments such as SL-disposition notes must never appear there (see
  // PRODUCT-275). Internal comments remain visible in the referral drawer's
  // comment thread, which uses a separate endpoint.
  if (filters.engagement_step === 'closed') {
    params.set('include_closed', 'true');
  }

  if (cursor) params.set('cursor', cursor);
  if (limit) params.set('limit', String(limit));
  if (search) params.set('search', search);
  if (ordering) params.set('ordering', ordering);
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  });

  return apiGet(`/api/v1/referrals/?${params.toString()}`);
};

/**
 * Fetch the single-select filter value-sets for the Carrier Engagement
 * dashboard columns from /api/v1/referrals/filter-options/.
 *
 * Response keys: clients, tpa_carriers, referral_types, referral_schemes,
 * c_scores, base_statuses, tpa_carrier_statuses, tpa_carrier_reasons,
 * sl_responses, sl_reasons, sl_actions. Each is a list of `{ value, label }`.
 * Independent of pagination and any applied filter, so it's loaded once.
 *
 * @returns {Promise<Object>}
 */
export const fetchEngagementFilterOptions = async () =>
  apiGet('/api/v1/referrals/filter-options/');
